import { ERROR_CODES } from '../constants';

// Error handling utilities
export interface AppError {
    code: string;
    message: string;
    details?: string;
    hint?: string;
}

// Error message mapping
const ERROR_MESSAGES: Record<string, string> = {
    [ERROR_CODES.TABLE_NOT_FOUND]: 'Database table not found. Please run database setup.',
    [ERROR_CODES.FOREIGN_KEY_CONSTRAINT]: 'User profile error. Please try signing out and back in.',
    [ERROR_CODES.UNIQUE_CONSTRAINT]: 'You already have an assistant. Each user can only create one assistant.',
    [ERROR_CODES.NOT_ACCEPTABLE]: 'There was an issue with the request. Please try again.',
    [ERROR_CODES.CONFLICT]: 'This name is already taken. Please choose a different name.',
    [ERROR_CODES.NO_ROWS_RETURNED]: 'No data found.',
};

// Default error message
const DEFAULT_ERROR_MESSAGE = 'An unexpected error occurred. Please try again.';

// Function to get user-friendly error message
export const getErrorMessage = (error: AppError | Error | any): string => {
    // Handle Supabase errors
    if (error?.code && ERROR_MESSAGES[error.code]) {
        return ERROR_MESSAGES[error.code];
    }

    // Handle specific constraint violations
    if (error?.message?.includes('duplicate key value violates unique constraint')) {
        if (error.message.includes('user_id')) {
            return 'You already have an assistant. Each user can only create one assistant.';
        }
        if (error.message.includes('unique_url')) {
            return 'This URL is already taken. Please try a different name.';
        }
        return 'This item already exists. Please try a different value.';
    }

    // Handle generic errors
    if (error instanceof Error) {
        return error.message;
    }

    // Handle string errors
    if (typeof error === 'string') {
        return error;
    }

    // Handle objects with message property
    if (error?.message) {
        return error.message;
    }

    return DEFAULT_ERROR_MESSAGE;
};

// Function to log error for debugging
export const logError = (context: string, error: AppError | Error | any): void => {
    console.error(`[${context}] Error:`, {
        code: error?.code,
        message: error?.message,
        details: error?.details,
        hint: error?.hint,
        stack: error?.stack,
    });
};

// Function to handle common Supabase errors
export const handleSupabaseError = (error: any, context: string): string => {
    logError(context, error);
    return getErrorMessage(error);
};

// Function to check if error is a specific type
export const isErrorType = (error: any, errorCode: string): boolean => {
    return error?.code === errorCode;
};

// Function to create a standardized error object
export const createError = (code: string, message: string, details?: string): AppError => ({
    code,
    message,
    details,
});

// Function to handle async operations with error handling
export const withErrorHandling = async <T>(
    operation: () => Promise<T>,
    context: string,
    fallback?: T
): Promise<T> => {
    try {
        return await operation();
    } catch (error) {
        logError(context, error);
        if (fallback !== undefined) {
            return fallback;
        }
        throw error;
    }
};
